import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { uniqueId } from 'lodash-es';
import { useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { cachedItemByIdSelector, infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  ActualExpensesCounterpartiesGrid,
  ActualExpensesCounterpartiesTotalParams,
  ActualExpensesCounterpartiesValidateModel,
  ActualExpensesMeteringPointDailyExpensesResponse,
  ActualExpensesMeteringPointGrid,
  ActualExpensesMeteringPointTotalsGrid,
  ActualExpensesMeteringPointsDailyExpensesByDateParams,
  ActualExpensesMeteringPointsDocuments,
  ActualExpensesMeteringPointsInfo,
  ActualExpensesMeteringPointsInfoFilter,
  ActualExpensesMeteringPointsParams,
  CleanActualExpensesMeteringPointDailyExpensesPayload,
  DailyExpensesByDate,
  DocumentParams,
  MeteringPointsDailyExpensesFillPayload,
  SaveActualExpensesMeteringPointDailyExpensesByDatePayload,
  UpdateActualExpensesMeteringPointDailyExpensesByDatePayload,
  VerifyDocumentDailyExpensesResponse,
} from './types';

export const actualExpensesApi = createApi({
  reducerPath: 'actualExpensesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/counterparty-actual-expenses',
  }),
  tagTypes: [
    'ActualExpensesCounterparties',
    'ActualExpensesMeteringPoints',
    'ActualExpensesMeteringPointsInfo',
    'ActualExpensesMeteringPointsDocuments',
    'ActualExpensesMeteringPointDailyExpenses',
    'ActualExpensesMeteringPointsTotals',
    'ActualExpensesCounterpartiesTotal',
  ],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getActualExpensesCounterparties: builder.infiniteQuery<
      ActualExpensesCounterpartiesGrid[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => {
        return {
          url: `/counterparties/query`,
          params: { ...queryArg, ...pageParam },
        };
      },
      providesTags: () => ['ActualExpensesCounterparties'],
      transformResponse: (response: ActualExpensesCounterpartiesGrid[]) => {
        // this id is for DataGrid only
        return response?.map(item => ({ ...item, id: uniqueId() }));
      },
    }),
    validateActualExpenses: builder.mutation<
      void,
      ActualExpensesCounterpartiesValidateModel
    >({
      query: data => ({
        url: '/validate',
        method: 'POST',
        data,
      }),
    }),
    getActualExpensesMeteringPoints: builder.infiniteQuery<
      ActualExpensesMeteringPointGrid[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => {
        return {
          url: '/metering-points/query',
          params: { ...queryArg, ...pageParam },
        };
      },
      providesTags: () => ['ActualExpensesMeteringPoints'],
      transformResponse: (response: ActualExpensesMeteringPointGrid[]) => {
        // this id is for DataGrid only
        return response?.map(item => ({
          ...item,
          id: uniqueId(),
        }));
      },
    }),
    getActualExpensesMeteringPointsTotals: builder.query<
      ActualExpensesMeteringPointTotalsGrid,
      FiltersGrid | void
    >({
      query: params => {
        return {
          url: '/metering-points/query/totals',
          params,
        };
      },
      providesTags: () => ['ActualExpensesMeteringPointsTotals'],
    }),
    getActualExpensesMeteringPointsInfo: builder.query<
      ActualExpensesMeteringPointsInfo,
      ActualExpensesMeteringPointsInfoFilter
    >({
      query: params => ({
        url: '/metering-points/info',
        method: 'GET',
        params,
      }),
      providesTags: () => ['ActualExpensesMeteringPointsInfo'],
    }),
    getActualExpensesMeteringPointsDocuments: builder.query<
      ActualExpensesMeteringPointsDocuments,
      ActualExpensesMeteringPointsParams
    >({
      query: params => ({
        url: '/documents',
        method: 'GET',
        params,
      }),
      providesTags: () => ['ActualExpensesMeteringPointsDocuments'],
    }),
    getActualExpensesMeteringPointDailyExpenses: builder.query<
      ActualExpensesMeteringPointDailyExpensesResponse,
      ActualExpensesMeteringPointsParams
    >({
      query: params => ({
        url: '/metering-points/daily-expenses/query',
        method: 'GET',
        params,
      }),
      providesTags: () => ['ActualExpensesMeteringPointDailyExpenses'],
    }),
    getActualExpensesMeteringPointDailyExpensesByDate: builder.query<
      DailyExpensesByDate,
      ActualExpensesMeteringPointsDailyExpensesByDateParams
    >({
      query: params => ({
        url: '/metering-points/daily-expenses',
        method: 'GET',
        params,
      }),
    }),
    getActualExpensesMeteringPointDailyExpensesById: builder.query<
      DailyExpensesByDate,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/metering-points/daily-expenses/${id}`,
        method: 'GET',
      }),
    }),
    saveActualExpensesMeteringPointDailyExpensesByDate: builder.mutation<
      string,
      SaveActualExpensesMeteringPointDailyExpensesByDatePayload
    >({
      query: data => ({
        url: '/metering-points/daily-expenses',
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        'ActualExpensesMeteringPointDailyExpenses',
        'ActualExpensesMeteringPointsDocuments',
        'ActualExpensesMeteringPoints',
        'ActualExpensesCounterparties',
        'ActualExpensesMeteringPointsTotals',
        'ActualExpensesCounterpartiesTotal',
      ],
    }),
    updateActualExpensesMeteringPointDailyExpensesByDate: builder.mutation<
      string,
      UpdateActualExpensesMeteringPointDailyExpensesByDatePayload
    >({
      query: ({ id, consumption }) => ({
        url: `/metering-points/daily-expenses/${id}`,
        method: 'PUT',
        data: { consumption },
      }),
      invalidatesTags: [
        'ActualExpensesMeteringPointDailyExpenses',
        'ActualExpensesMeteringPointsDocuments',
        'ActualExpensesMeteringPoints',
        'ActualExpensesCounterparties',
        'ActualExpensesMeteringPointsTotals',
        'ActualExpensesCounterpartiesTotal',
      ],
    }),
    checkFillMeteringPointsDailyExpenses: builder.query<
      boolean,
      MeteringPointsDailyExpensesFillPayload
    >({
      query: params => ({
        url: `/metering-points/daily-expenses/fill-accessible`,
        params,
      }),
    }),
    validateMeteringPointsDailyExpensesFillAccessible: builder.mutation<
      string,
      MeteringPointsDailyExpensesFillPayload
    >({
      query: data => ({
        url: `/metering-points/daily-expenses/validate`,
        method: 'POST',
        data,
      }),
    }),
    fillByCustomerDataAccessible: builder.query<
      { disable: boolean },
      DocumentParams
    >({
      query: params => ({
        url: `/metering-points/daily-expenses/fill-by-customer-data-accessible`,
        params,
      }),
    }),
    fillMeteringPointsDailyExpenses: builder.mutation<
      string,
      MeteringPointsDailyExpensesFillPayload
    >({
      query: data => ({
        url: `/metering-points/daily-expenses/fill-by-operational-expenses`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        'ActualExpensesMeteringPointDailyExpenses',
        'ActualExpensesMeteringPointsDocuments',
        'ActualExpensesMeteringPoints',
        'ActualExpensesMeteringPointsTotals',
        'ActualExpensesCounterparties',
        'ActualExpensesCounterpartiesTotal',
      ],
    }),
    cleanActualExpensesMeteringPointDailyExpenses: builder.mutation<
      string,
      CleanActualExpensesMeteringPointDailyExpensesPayload
    >({
      query: params => ({
        url: `/metering-points/daily-expenses/clean`,
        method: 'POST',
        params,
      }),
      invalidatesTags: [
        'ActualExpensesMeteringPointDailyExpenses',
        'ActualExpensesMeteringPointsDocuments',
        'ActualExpensesMeteringPoints',
        'ActualExpensesCounterparties',
        'ActualExpensesMeteringPointsTotals',
        'ActualExpensesCounterpartiesTotal',
      ],
    }),
    fillByCustomerData: builder.mutation<any, DocumentParams>({
      query: data => ({
        url: `/metering-points/daily-expenses/fill-by-customer-data`,
        method: 'POST',
        data,
      }),
    }),
    documentsExist: builder.query<boolean, DocumentParams>({
      query: params => ({
        url: `/documents/exists`,
        params,
      }),
    }),
    verifyActualExpensesMeteringPointDailyExpenses: builder.query<
      VerifyDocumentDailyExpensesResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/documents/${id}/verify`,
      }),
    }),
    confirmActualExpensesMeteringPointDailyExpenses: builder.mutation<
      VerifyDocumentDailyExpensesResponse,
      { id: string; ignoreImbalance: boolean }
    >({
      query: ({ id, ignoreImbalance }) => ({
        url: `/documents/${id}/confirm`,
        method: 'PUT',
        params: { ignoreImbalance },
      }),
      invalidatesTags: [
        'ActualExpensesMeteringPointsDocuments',
        'ActualExpensesMeteringPoints',
      ],
    }),
    getActualExpensesCounterpartiesTotal: builder.query<
      ActualExpensesMeteringPointTotalsGrid,
      ActualExpensesCounterpartiesTotalParams
    >({
      query: params => ({
        url: '/counterparties/query/totals',
        method: 'GET',
        params,
      }),
      providesTags: () => ['ActualExpensesCounterpartiesTotal'],
    }),
  }),
});

export const {
  useValidateActualExpensesMutation,
  useGetActualExpensesMeteringPointsInfoQuery,
  useGetActualExpensesMeteringPointsDocumentsQuery,
  useGetActualExpensesMeteringPointDailyExpensesQuery,
  useGetActualExpensesMeteringPointDailyExpensesByIdQuery,
  useSaveActualExpensesMeteringPointDailyExpensesByDateMutation,
  useUpdateActualExpensesMeteringPointDailyExpensesByDateMutation,
  useValidateMeteringPointsDailyExpensesFillAccessibleMutation,
  useCheckFillMeteringPointsDailyExpensesQuery,
  useFillMeteringPointsDailyExpensesMutation,
  useCleanActualExpensesMeteringPointDailyExpensesMutation,
  useVerifyActualExpensesMeteringPointDailyExpensesQuery,
  useLazyVerifyActualExpensesMeteringPointDailyExpensesQuery,
  useConfirmActualExpensesMeteringPointDailyExpensesMutation,
  useFillByCustomerDataAccessibleQuery,
  useLazyDocumentsExistQuery,
  useFillByCustomerDataMutation,
  useGetActualExpensesCounterpartiesTotalQuery,
  useGetActualExpensesMeteringPointsTotalsQuery,
} = actualExpensesApi;

export const useGetActualExpensesCounterpartiesIInfiniteQuery =
  normalizeInfiniteQueryData(
    actualExpensesApi.useGetActualExpensesCounterpartiesInfiniteQuery,
  );

export const useGetActualExpensesMeteringPointsIInfiniteQuery =
  normalizeInfiniteQueryData(
    actualExpensesApi.useGetActualExpensesMeteringPointsInfiniteQuery,
  );

const getCacheActualExpensesCounterpartyByIdSelector = (
  counterpartyId: string,
  contractId: string,
) =>
  cachedItemByIdSelector(
    'actualExpensesApi',
    'getActualExpensesCounterpartiesI',
    p => p.contractId === contractId && p.counterpartyId === counterpartyId,
  )('');

export const useGetCachedActualExpensesCounterpartyByIdSelector = ({
  counterpartyId,
  contractId,
}) =>
  useSelector(
    getCacheActualExpensesCounterpartyByIdSelector(counterpartyId, contractId),
  );

const getCacheActualExpensesMPByIdSelector = cachedItemByIdSelector(
  'actualExpensesApi',
  'getActualExpensesMeteringPointsI(',
  'commercialMeteringPointId',
);

export const useGetCachedActualExpensesMPByIdSelector = (
  commercialMeteringPointId: string,
) =>
  useSelector(getCacheActualExpensesMPByIdSelector(commercialMeteringPointId));
